import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import ClientForm, { getClientError, useClientState } from '@/components/ClientForm';
import { useNavigate } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconArrow from '@/icons/Arrow.svg';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { useUpdateRequestClient, useUpdateRequestType } from '@/models/request';
import { ProgressBar } from '@/routes/Brand/Requests/New/components/ProgressBar/ProgressBar';
import { createBEMClasses } from '@/utils/classname';
import { REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY } from '@/utils/requestInProgress';
import useViewPort from '@/utils/useViewport';

import './SettingsForm.css';

const { block } = createBEMClasses('client-settings-form');

const SettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { isMobile } = useViewPort();
  const { request, view } = useBrandRequestContext();

  const selectedType = request.client ? 'client' : 'store';
  const { mutateAsync: updateRequestType } = useUpdateRequestType(request.id);
  const { mutateAsync: updateRequestClient, isPending: isUpdateRequestClientPending } =
    useUpdateRequestClient(request.id);

  const [showErrors, setShowErrors] = useState(false);
  const clientState = useClientState();

  const handleContinue = async () => {
    setShowErrors(true);

    if (selectedType === 'client') {
      if (getClientError({ client: clientState, view: view.client }).hasError) {
        return;
      }

      await updateRequestClient({
        client: {
          name: clientState.name,
          title: clientState.title,
          firstName: clientState.firstName,
          lastName: clientState.lastName,
          email: clientState.email,
          address: clientState.address ?? undefined,
          billingAddress: clientState.billingAddress ?? undefined,
          phone: clientState.phone,
          deliveryOption: clientState.deliveryOption,
          sendcloudServicePointId: clientState.sendcloudServicePointId,
          locale: clientState.locale,
        },
      });

      onContinue();
    } else if (selectedType === 'store') {
      onContinue();
    }
  };

  const requestTypeOptions = [
    {
      id: 'client',
      text: t({ id: 'client.new.settings.type.client', message: 'Client' }),
    },
    {
      id: 'store',
      text: t({ id: 'client.new.settings.type.store', message: 'Store' }),
    },
  ];

  return (
    <div className={block()}>
      <main>
        <Stack gap="1.5rem">
          <Title size="300" as="h3">
            <Trans id="client.new.settings.title">Fill the request information</Trans>
          </Title>
          <Stack gap="1rem">
            <InputSelect
              variant="select"
              label={t({ id: 'client.new.settings.type.label', message: 'Request type' })}
              isDisabled={!request.draft}
              isSearchable={false}
              options={requestTypeOptions}
              value={requestTypeOptions.find(
                (requestTypeOption) => selectedType === requestTypeOption.id
              )}
              getOptionValue={(requestTypeOption) => requestTypeOption.text}
              getOptionLabel={(requestTypeOption) => requestTypeOption.text}
              onChange={(requestTypeOption) => {
                if (requestTypeOption && requestTypeOption.id !== selectedType) {
                  updateRequestType({ type: requestTypeOption.id as 'client' | 'store' });
                }
              }}
              styleVariant="brand"
              size="large"
            />

            {selectedType === 'client' && (
              <ClientForm
                state={clientState}
                hideVip
                variant="brand"
                size="large"
                showErrors={showErrors}
              />
            )}
          </Stack>
        </Stack>
      </main>
      <footer>
        <ProgressBar progress={5} />
        <Stack row gap={isMobile ? '1rem' : '2rem'}>
          <Button
            size="large"
            variant="secondary-brand"
            style={{ flex: 1 }}
            onPress={() => {
              window.localStorage.removeItem(REQUEST_IN_PROGRESS_LOCAL_STORAGE_KEY);
              navigate(`/brand/${request.organization.slug}/requests/new`, { replace: true });
            }}
          >
            <IconArrow left />
            <Trans id="client.new.settings.action.back">Back</Trans>
          </Button>
          <Button
            size="large"
            variant="brand"
            style={{ flex: 1 }}
            isLoading={isUpdateRequestClientPending}
            onPress={handleContinue}
          >
            <Trans id="client.new.settings.action.continue">Continue</Trans>
            <IconArrow right />
          </Button>
        </Stack>
      </footer>
    </div>
  );
};

export default SettingsForm;
